import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Trash2, Plus, Save, Mic, Loader2, PackagePlus, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface StagedProduct {
  id: string;
  name: string;
  qty: string;
  unit: string;
  cost: string;
  selling: string;
  category: string;
}

interface BulkInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  onProductsAdded: (count: number) => void;
  initialProducts?: StagedProduct[];
}

const UNIT_OPTIONS = ['Pieces', 'Kg', 'Grams', 'Liters', 'ml'];
const UNIT_SHORT: Record<string, string> = { Pieces: 'Pcs', Kg: 'Kg', Grams: 'g', Liters: 'L', ml: 'ml' };

const EMPTY_ROW = (): StagedProduct => ({
  id: `row_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  name: '', qty: '', unit: 'Pieces', cost: '', selling: '', category: 'Grocery',
});

export default function BulkInventoryModal({ isOpen, onClose, storeApiKey, onProductsAdded, initialProducts }: BulkInventoryModalProps) {
  const [rows, setRows] = useState<StagedProduct[]>([EMPTY_ROW()]);
  const [transcript, setTranscript] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [parseError, setParseError] = useState('');
  const [saveResult, setSaveResult] = useState<{ count: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Accept initialProducts from Luna voice (via event)
  useEffect(() => {
    if (initialProducts && initialProducts.length > 0) {
      setRows(initialProducts);
    }
  }, [initialProducts]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && !initialProducts?.length) {
      setRows([EMPTY_ROW()]);
      setTranscript('');
      setParseError('');
      setSaveResult(null);
    }
  }, [isOpen]);

  // Listen for Luna bulk-inventory events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.action === 'CONFIRM_SAVE' && rows.length > 0) {
        handleSaveAll();
      }
    };
    window.addEventListener('luna-bulk-inventory', handler);
    return () => window.removeEventListener('luna-bulk-inventory', handler);
  }, [rows, storeApiKey]);

  const handleParse = async () => {
    if (!transcript.trim()) return;
    setIsParsing(true);
    setParseError('');
    try {
      const res = await fetch('/api/ai/parse-inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: transcript.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.products?.length) {
        setParseError(data.error || 'No products detected in transcript. Try rewording.');
        return;
      }
      const parsed: StagedProduct[] = data.products.map((p: any) => ({
        id: `row_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        name: p.name || '',
        qty: String(p.qty ?? ''),
        unit: UNIT_OPTIONS.includes(p.unit) ? p.unit : 'Pieces',
        cost: p.cost != null ? String(p.cost) : '',
        selling: String(p.selling ?? ''),
        category: p.category || 'Grocery',
      }));
      setRows(parsed);
    } catch {
      setParseError('Network error — cannot reach AI parser');
    } finally {
      setIsParsing(false);
    }
  };

  const handleSaveAll = async () => {
    const valid = rows.filter(r => r.name.trim() && r.selling);
    if (valid.length === 0) return;
    setIsSaving(true);
    setSaveResult(null);
    try {
      const res = await fetch('/api/products/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey: storeApiKey,
          products: valid.map(r => ({
            name: r.name.trim(),
            qty: parseFloat(r.qty) || 0,
            unit: r.unit,
            cost: r.cost ? parseFloat(r.cost) : null,
            selling: parseFloat(r.selling) || 0,
            category: r.category,
          })),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSaveResult({ count: data.count });
        onProductsAdded(data.count);
        setTimeout(() => { onClose(); }, 1800);
      } else {
        setParseError(data.error || 'Save failed');
      }
    } catch {
      setParseError('Network error — save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const updateRow = (id: string, field: keyof StagedProduct, value: string) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const removeRow = (id: string) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const addRow = () => {
    setRows(prev => [...prev, EMPTY_ROW()]);
  };

  if (!isOpen) return null;

  const validCount = rows.filter(r => r.name.trim() && r.selling).length;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-xl flex items-center justify-center p-3"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        className="relative w-full max-w-4xl max-h-[90vh] rounded-3xl bg-[#0a0e1a] border border-violet-500/25 shadow-[0_0_60px_rgba(139,92,246,0.2)] flex flex-col overflow-hidden"
      >
        {/* Ambient */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-[300px] h-[300px] rounded-full bg-violet-500/8 blur-[100px]" />
          <div className="absolute -bottom-20 -right-20 w-[300px] h-[300px] rounded-full bg-cyan-500/6 blur-[100px]" />
        </div>

        {/* Header */}
        <div className="relative z-10 flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
              <PackagePlus size={16} className="text-violet-400" />
            </div>
            <div>
              <h2 className="text-sm font-black text-white tracking-wide">AI Bulk Inventory</h2>
              <p className="text-[9px] text-slate-600 font-mono">Dictate or paste — Luna parses, you review, one-click save</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/8 text-slate-600 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Voice / Text Input */}
        <div className="relative z-10 px-5 py-3 border-b border-white/[0.04]">
          <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono mb-1.5 block">
            <Mic size={10} className="inline mr-1" /> Paste transcript or type inventory list
          </label>
          <div className="flex gap-2">
            <textarea
              ref={textareaRef}
              value={transcript}
              onChange={e => setTranscript(e.target.value)}
              placeholder="e.g. Samba rice 1000kg, in 230, out 250. Sugar 500kg, in 100, out 120. Milk 50 pieces, out 350..."
              rows={2}
              className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white placeholder:text-slate-700 outline-none focus:border-violet-500/40 font-mono resize-none"
            />
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleParse}
              disabled={isParsing || !transcript.trim()}
              className="px-4 rounded-xl bg-violet-500/20 border border-violet-500/40 text-violet-300 text-[10px] font-bold uppercase tracking-wider hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1.5 shrink-0"
            >
              {isParsing ? <Loader2 size={12} className="animate-spin" /> : <PackagePlus size={12} />}
              {isParsing ? 'Parsing...' : 'Parse'}
            </motion.button>
          </div>
          {parseError && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
              className="mt-2 flex items-center gap-1.5 text-[10px] text-red-400 font-mono">
              <AlertTriangle size={11} /> {parseError}
            </motion.div>
          )}
        </div>

        {/* Staging Table */}
        <div className="relative z-10 flex-1 overflow-auto px-5 py-3">
          {saveResult ? (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="flex flex-col items-center justify-center h-full gap-3">
              <CheckCircle2 size={48} className="text-emerald-400" />
              <p className="text-lg font-black text-white">{saveResult.count} products saved!</p>
              <p className="text-[10px] text-slate-600 font-mono">Inventory updated successfully</p>
            </motion.div>
          ) : (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-[8px] text-slate-600 uppercase tracking-widest font-mono border-b border-white/[0.06]">
                  <th className="text-left py-2 px-1 w-[28%]">Product Name</th>
                  <th className="text-left py-2 px-1 w-[10%]">Qty</th>
                  <th className="text-left py-2 px-1 w-[12%]">Unit</th>
                  <th className="text-left py-2 px-1 w-[14%]">Cost (In)</th>
                  <th className="text-left py-2 px-1 w-[14%]">Sell (Out)</th>
                  <th className="text-left py-2 px-1 w-[14%]">Category</th>
                  <th className="w-[8%]"></th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {rows.map((row) => (
                    <motion.tr
                      key={row.id}
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                      className="border-b border-white/[0.03] hover:bg-white/[0.02]"
                    >
                      <td className="py-1.5 px-1">
                        <input value={row.name} onChange={e => updateRow(row.id, 'name', e.target.value)}
                          placeholder="Product name"
                          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1.5 text-white placeholder:text-slate-800 outline-none focus:border-violet-500/40 font-mono text-[11px]" />
                      </td>
                      <td className="py-1.5 px-1">
                        <input value={row.qty} onChange={e => updateRow(row.id, 'qty', e.target.value)}
                          type="number" min="0" step="any" placeholder="0"
                          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1.5 text-white placeholder:text-slate-800 outline-none focus:border-emerald-500/40 font-mono text-[11px]" />
                      </td>
                      <td className="py-1.5 px-1">
                        <select value={row.unit} onChange={e => updateRow(row.id, 'unit', e.target.value)}
                          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-1 py-1.5 text-white outline-none font-mono text-[10px]">
                          {UNIT_OPTIONS.map(u => (
                            <option key={u} value={u} className="bg-[#080b18]">{UNIT_SHORT[u]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1.5 px-1">
                        <input value={row.cost} onChange={e => updateRow(row.id, 'cost', e.target.value)}
                          type="number" min="0" step="0.01" placeholder="Cost"
                          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1.5 text-orange-300 placeholder:text-slate-800 outline-none focus:border-orange-500/40 font-mono text-[11px]" />
                      </td>
                      <td className="py-1.5 px-1">
                        <input value={row.selling} onChange={e => updateRow(row.id, 'selling', e.target.value)}
                          type="number" min="0" step="0.01" placeholder="Sell *"
                          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1.5 text-emerald-300 placeholder:text-slate-800 outline-none focus:border-emerald-500/40 font-mono text-[11px]" />
                      </td>
                      <td className="py-1.5 px-1">
                        <input value={row.category} onChange={e => updateRow(row.id, 'category', e.target.value)}
                          placeholder="Grocery"
                          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1.5 text-white placeholder:text-slate-800 outline-none focus:border-white/20 font-mono text-[10px]" />
                      </td>
                      <td className="py-1.5 px-1 text-center">
                        <button onClick={() => removeRow(row.id)}
                          className="p-1.5 rounded-lg hover:bg-red-500/20 text-slate-700 hover:text-red-400 transition-colors">
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {!saveResult && (
          <div className="relative z-10 flex items-center justify-between px-5 py-3 border-t border-white/[0.06]">
            <button onClick={addRow}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.08] text-slate-500 hover:text-white hover:border-white/20 text-[10px] font-bold transition-all">
              <Plus size={12} /> Add Row
            </button>
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-slate-600 font-mono">{validCount} valid item{validCount !== 1 ? 's' : ''}</span>
              <button onClick={onClose}
                className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/[0.08] text-slate-500 hover:text-white text-[10px] font-bold transition-all">
                Discard
              </button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleSaveAll}
                disabled={isSaving || validCount === 0}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-black text-[10px] font-black uppercase tracking-wider shadow-[0_0_16px_rgba(16,185,129,0.3)] hover:shadow-[0_0_28px_rgba(16,185,129,0.5)] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save All ({validCount})
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
